
import OktaSignIn from "@bend-financial/okta-signin-widget";
import oktaConfig from "@/okta-config";
import { OktaAuth as Auth } from "@/shared/api/auth";
import { login as oktaLoginResource } from "@/shared/oktaResources";
import { useUserStore } from "@/shared/store/user";
import { useThemeStore } from "@/shared/store/theme";
import { AuthType } from "@/shared/types";
import * as datadog from "@/shared/datadog";
import "@bend-financial/okta-signin-widget/dist/css/okta-sign-in.min.css";

export class OktaSignInWidgetBuilder {
  private widget: OktaSignIn;
  private signInResponse: { data: any };
  private config: {};
  private oidc;
  private features;
  private signinElement;

  constructor (el) {
    const userStore = useUserStore();
    const themeStore = useThemeStore();

    this.widget = null;
    this.signinElement = el;

    const site = userStore.site.toLowerCase();
    this.oidc = oktaConfig[site];
    this.features = oktaConfig.features[site];

    /** set configurations */
    this.config = {
      el: this.signinElement,
      baseUrl: this.oidc.issuer.split("/oauth2")[0],
      clientId: this.oidc.clientId,
      redirectUri: this.oidc.redirectUri,
      i18n: oktaLoginResource,
      authParams: {
        issuer: this.oidc.issuer,
        scopes: this.oidc.scopes
      },
      features: this.features,
      colors: { brand: themeStore.primaryColor },
      cookies: {
        secure: true
      },
      helpLinks: {},
      customButtons: []
    };
  }

  setConfig (config: any): this {
    this.widget = new OktaSignIn(config);
    return this;
  }

  setUsername (username: string): this {
    this.config = { ...this.config, username };
    return this;
  }

  setBrandColor (color: any): this {
    this.config = { ...this.config, colors: { brand: color } };
    return this;
  }

  setForgotPasswordLink (forgotPassworkLink: string): this {
    this.config = {...this.config,
      helpLinks: {
        forgotPassword: forgotPassworkLink,
      }
    };
    return this;
  }

  overrideLoginProperties (loginProperties): this {
    this.config = { ...this.config,
      i18n: loginProperties
    };
    return this;
  }

  setLogo (imgSrc) {
    this.config = { ...this.config,
      logo: imgSrc
    };
    return this;
  }

  onCustomCancelButtonClicked (callback) {
    this.config = {
      ... this.config, customButtons: [{
        title: "Cancel",
        className: "customButtonCancel",
        click: () => {
          this.removeSignInWidget();
          callback();
        }
      }]
    };
    return this;
  }

  build (): this {
    this.widget = new OktaSignIn(this.config);
    this.widget.on("pageRendered", context => this.pageRenderedEvents(context));
    return this;
  }

  buildForOktaFactorSettings (): this {
    this.config = { ...this.config,
      features: { rememberMe: false, showPasswordToggleOnSignInPage: true },
      i18n: { en: { "primaryauth.title": "For your security, please reauthenticate to manage your authentication settings." }}
    };
    this.widget = new OktaSignIn(this.config);
    this.widget.on("afterRender", context => this.afterRenderEvents(context));
    return this;
  }

  buildForBiometricAuthSetup (): this {
    this.config = { ...this.config,
      features: { rememberMe: false, showPasswordToggleOnSignInPage: true },
      i18n: { en: { "primaryauth.title": "For your security, please reauthenticate to manage your authentication settings." }}
    };
    this.widget = new OktaSignIn(this.config);
    this.widget.on("afterRender", context => this.afterRendEventsForBiometricAuthSetup(context));
    return this;
  }

  buildForLoginWithoutPrompt (username: string, password: string): this {
    this.config = { ...this.config,
      features: { rememberMe: false, idpDiscovery: false },
    };
    this.widget = new OktaSignIn(this.config);
    this.widget.on("pageRendered", () => {
      this.setUsernameAttribute(username);
      this.setPasswordAttribute(password);
      document.getElementById("okta-signin-submit").click();
    });
    return this;
  }

  afterRenderEvents (context) {
    this.hideUsername(context);
    this.setupCustomCancelButton(context);
    this.setupMfaFactorSelectionView(context);
    this.reorganizeHelpLinks(context);
  }

  pageRenderedEvents (context) {
    this.reorganizeHelpLinks(context);
  }

  afterRendEventsForBiometricAuthSetup (context) {
    this.afterRenderEvents(context);
    this.storePasswordOnSigninSubmit(context);
  }

  showSignIn (): Promise<any> {
    return this.widget
    .showSignIn()
    .then(() => {
      return Promise.resolve(this.signInResponse);
    })
    .finally(() =>
      this.removeSignInWidget()
    );
  }

  signInWithoutPromptAndSetTokens (username: string, password: string): Promise<void> {
    return new Promise((resolve, reject) => {
      this.config = { ...this.config,
        features: { rememberMe: false, idpDiscovery: false },
      };

      this.widget = new OktaSignIn(this.config);

      this.widget.on("pageRendered", () => {
        this.setUsernameAttribute(username);
        this.setPasswordAttribute(password);
        const submitBtn = document.getElementById("okta-signin-submit");
        submitBtn?.click();
      });

      this.widget.showSignInToGetTokens()
      .then(response => {
        const userStore = useUserStore();
        userStore.setAuthType(AuthType.Okta);
        Auth().setTokens(response);
        resolve();
      });

      // Okta login error
      this.widget.on("afterError", error => {
        datadog.log(`Login failed for loginUsername: ${username}.`, error, "error");
        reject(new Error(error));
      });
    });
  }

  showSignInAndSetTokens (): Promise<any> {
    return this.widget.showSignInToGetTokens()
    .then(response => {
      const userStore = useUserStore();
      userStore.setAuthType(AuthType.Okta);
      Auth().setTokens(response);
    });
  }

  setupMfaFactorSelectionView (context) {
    if (context.controller === "mfa-verify") {
      // display mfa factor selection
      this.removeDOMElementAttributeByClass("okta-sign-in-header auth-header", "style");
    }
  }

  storePasswordOnSigninSubmit (context) {
    if (context && context.controller === "primary-auth") {
      const submitButton = document.getElementById("okta-signin-submit");
      const passwordElement: HTMLInputElement = document.getElementById("okta-signin-password") as any;
      submitButton.addEventListener("click", _ => {
        this.signInResponse = { data: passwordElement.value };
      });
    }
  }

  hideUsername (context) {
    if (context && context.controller === "primary-auth") {
      // headerElement (this is a parent of username element)
      this.hideDOMElementByClass("okta-sign-in-header auth-header");
      // usernameElement
      this.hideDOMElementByClass("o-form-fieldset o-form-label-top margin-btm-5");
    }
  }

  setupCustomCancelButton (context) {
    if (context && context.controller === "primary-auth") {
      // custom button divider
      this.hideDOMElementByClass("auth-divider");
      // custom button
      this.removeDOMElementAttributeByClass("customButtonCancel", "class");
      // footerElement
      this.hideDOMElementByClass("auth-footer");
    }
  }

  reorganizeHelpLinks (context) {
    // Early return if idpDiscovery is enabled and we're not on the idp-discovery page
    if (this.features.idpDiscovery && context.page !== "idp-discovery") {
      return;
    }

    // Group element selections and their modifications
    const elements = {
      needHelpSigningIn: document.getElementsByClassName("link help js-help")[0],
      helpLinksContainer: document.getElementById("help-links-container"),
      helpLink: document.getElementsByClassName("link js-help-link")[0]
    };

    // Apply modifications to elements if they exist
    if (elements.needHelpSigningIn) {
      elements.needHelpSigningIn.setAttribute("style", "display: none;");
    }

    if (elements.helpLinksContainer) {
      elements.helpLinksContainer.removeAttribute("style");
    }

    if (elements.helpLink && elements.helpLink.parentElement) {
      elements.helpLink.parentElement.remove();
    }
  }

  hideDOMElementByClass (className: string) {
    const element = document.getElementsByClassName(className)[0];
    if (element) {
      element.setAttribute("style", "display:none;");
    }
  }

  removeDOMElementAttributeByClass (className: string, attribute: string) {
    const element = document.getElementsByClassName(className)[0];
    if (element) {
      element.removeAttribute(attribute);
    }
  }

  setUsernameAttribute (username: string) {
    return new Promise<void>((resolve, reject) => {
      const usernameElement: HTMLInputElement = document.getElementById("okta-signin-username") as any;
      if (usernameElement) {
        usernameElement.value = username;
        usernameElement.dispatchEvent(new Event("input", { bubbles: true }));
        resolve();
        return;
      }
      reject();
    });
  }

  setPasswordAttribute (password: string) {
    return new Promise<void>((resolve, reject) => {
      const passwordElement: HTMLInputElement = document.getElementById("okta-signin-password") as any;
      if (passwordElement) {
        passwordElement.value = password;
        passwordElement.dispatchEvent(new Event("keydown", { bubbles: true }));
        resolve();
        return;
      }
      reject();
    });
  }

  removeSignInWidget () {
    if (this.widget) {
      this.widget.off();
      this.widget.remove();
    }
  }

  destroyed () {
    this.removeSignInWidget();
  }
}